import type { PageTabConstants, PageTabItem } from "./pageTabs.types";

export const ALL_TAB_KEY = "all";
const PAGE_TAB_KEY_PREFIX = "page:";

// Matches a leading enumeration prefix like "4. ", "1) ", or "3 - " on a raw
// eCRF/form-name page value, so the tab label can drop it while the pin
// value keeps the original name (the backend still expects it verbatim).
const ENUMERATION_PREFIX_REGEX = /^\s*\d+\s*(?:[.)]\s*|\s+-\s+)/;

export function formatPageTabLabel(name: string): string {
  const cleaned = name.replace(ENUMERATION_PREFIX_REGEX, "").trim();
  return cleaned || name;
}

/**
 * Orders numerically-enumerated page names ("1. Vitals", "2. Labs", ...) by
 * their number first, then appends any non-enumerated names in their
 * original order.
 */
export function sortPageNames(names: string[]): string[] {
  const enumerated: { name: string; order: number }[] = [];
  const unenumerated: string[] = [];

  names.forEach((name) => {
    const match = name.match(ENUMERATION_PREFIX_REGEX);
    if (!match) {
      unenumerated.push(name);
      return;
    }
    const numberMatch = match[0].match(/\d+/);
    enumerated.push({
      name,
      order: numberMatch ? Number(numberMatch[0]) : 0,
    });
  });

  enumerated.sort((a, b) => a.order - b.order);

  return [...enumerated.map((item) => item.name), ...unenumerated];
}

/**
 * Maps each raw page name to its display label, falling back to the raw
 * name for any pair of names whose cleaned labels collide (e.g. "1. Vitals"
 * and "1) Vitals" would otherwise both become "Vitals").
 */
export function buildTabLabels(names: string[]): Map<string, string> {
  const cleanedByName = new Map(
    names.map((name) => [name, formatPageTabLabel(name)]),
  );
  const cleanedCounts = new Map<string, number>();
  cleanedByName.forEach((cleaned) => {
    cleanedCounts.set(cleaned, (cleanedCounts.get(cleaned) || 0) + 1);
  });

  return new Map(
    names.map((name) => {
      const cleaned = cleanedByName.get(name) as string;
      const label = (cleanedCounts.get(cleaned) || 0) > 1 ? name : cleaned;
      return [name, label];
    }),
  );
}

export function getPageTabItems(
  constants: PageTabConstants,
  pageNames: string[],
  pageFilterKey: string,
): PageTabItem[] {
  const sortedNames = sortPageNames(pageNames);
  const labelsByName = buildTabLabels(sortedNames);

  return [
    { key: ALL_TAB_KEY, label: "All", constants },
    ...sortedNames.map((name) => ({
      key: `${PAGE_TAB_KEY_PREFIX}${name}`,
      label: labelsByName.get(name) ?? name,
      constants,
      pinnedFilters: { [pageFilterKey]: [name] },
    })),
  ];
}
